// Phase 9 Plan 04 Task 1 — Closed 4-slot vocabulary system (D-13/D-14/D-15).
//
// Adding a new slot is a DELIBERATE BREAKING CHANGE reviewed at phase planning.
// The closed enum is the single source of truth shared across:
//   - apps/api/src/adapters/storage-postgres/schema.ts → vocabularyOverrides
//     column's $type<>() narrowing (Plan 09-02 file header documents the
//     post-09-04 import swap directive).
//   - apps/web/src/lib/vocabulary/useTerm.ts → default fallback when no
//     per-spread override is present.
//   - packages/shared/src/vocabulary/substituteExplanation.ts → fixed
//     token grammar regex (4-slot * 3-modifier closed grammar).
//   - downstream constraint-authoring UI (Plan 09-07) → label rendering.
//
// VOCABULARY_DEFAULTS is wrapped with Object.freeze so a downstream caller who
// attempts to mutate the default (e.g. `VOCABULARY_DEFAULTS.container = "Meal"`)
// fails LOUDLY in strict mode rather than silently shifting the fallback for
// every consumer in the process. SSR-safety analysis (RESEARCH §"useTerm
// SSR-safety analysis") relies on this referential stability across the
// client/SSR boundary in Phase 13 share-link rendering.
//
// VOCABULARY_SLOT_MAX_CHARS = 50 mirrors the per-slot DB CHECK in
// 0019_phase_9_spreads.sql. Mission Critical: keep the two byte-identical.
//
// isolatedDeclarations: paranoid-strict tsconfig requires explicit type
// annotations on exported consts that are not trivially inferrable. The
// `as const` tuple, the `Readonly<Record<...>>` typed default, and the
// number-literal MAX_CHARS each carry their annotation explicitly so tsc
// emits declarations without crossing module boundaries.

// Canonical 4-slot enum — `as const` makes the tuple literal-typed; the
// `typeof X[number]` derivation below produces the closed VocabularySlot union.
export const VOCABULARY_SLOTS = ["container", "subdivision", "set", "item"] as const;

// Closed literal-union — exhaustive switch over VocabularySlot is a
// compile-time check that no slot was forgotten in a downstream consumer.
export type VocabularySlot = (typeof VOCABULARY_SLOTS)[number];

// Frozen defaults — Object.freeze ensures the runtime object is immutable.
// Readonly<Record<VocabularySlot, string>> documents the type-level intent.
// The explicit type annotation on the const satisfies isolatedDeclarations.
export const VOCABULARY_DEFAULTS: Readonly<Record<VocabularySlot, string>> = Object.freeze({
  container: "Spread",
  subdivision: "Batch Set",
  set: "Set",
  item: "Item",
});

// Per-slot maximum character count — matches the DB CHECK in
// 0019_phase_9_spreads.sql to keep app-layer and DB-layer bounds aligned.
// Explicit `: number` annotation per isolatedDeclarations (the literal 50
// would otherwise widen `as const` semantics).
export const VOCABULARY_SLOT_MAX_CHARS: number = 50;
